import { inject, Injectable, signal } from '@angular/core';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../../amplify/data/resource';
import { getCurrentUser } from 'aws-amplify/auth';
import { invoke } from '@tauri-apps/api/core';
import { listen } from '@tauri-apps/api/event';
import { openUrl } from '@tauri-apps/plugin-opener';

const CALENDAR_SCOPE = 'https://www.googleapis.com/auth/calendar.readonly';
const CALENDAR_API_BASE = 'https://www.googleapis.com/calendar/v3';

export interface GoogleCalendarEvent {
  id: string;
  summary: string;
  description?: string;
  start: { dateTime?: string; date?: string };
  end: { dateTime?: string; date?: string };
  colorId?: string;
  calendarId?: string;
}

export interface GoogleCalendarInfo {
  id: string;
  summary: string;
  backgroundColor: string;
  primary?: boolean;
  accessRole: string;
}

@Injectable({ providedIn: 'root' })
export class GoogleCalendarService {
  private client = generateClient<Schema>();

  /** Reactive state — components can subscribe */
  connected = signal(false);
  connecting = signal(false);
  googleEmail = signal<string | null>(null);
  isDemoMode = signal(false);
  calendarList = signal<GoogleCalendarInfo[]>([]);
  enabledCalendarIds = signal<string[] | null>(null); // null = all enabled

  private accessToken: string | null = null;
  private tokenExpiry: number = 0;
  private settingsId: string | null = null;
  private connectedPlatform: 'desktop' | 'web' = 'desktop';


  // ─── Initialise from stored settings ────────────────────────────────────
  async init(settingsId: string): Promise<void> {
    this.settingsId = settingsId;
    try {
      const { data } = await this.client.models.UserSettings.get({ id: settingsId });
      if (data?.googleCalendarConnected && data.googleCalendarRefreshToken) {
        // ✅ Mark connected immediately — don't block on the Lambda refresh
        this.connected.set(true);

        // Restore enabled calendar IDs (AWSJSON comes back as a JSON string from AppSync)
        const raw = data.googleCalendarEnabledIds;
        let savedIds: string[] | null = null;
        if (raw !== null && raw !== undefined) {
          try {
            savedIds = typeof raw === 'string' ? JSON.parse(raw) : raw as string[];
          } catch {
            console.warn('[GoogleCalendar] Could not parse googleCalendarEnabledIds:', raw);
          }
        }
        this.enabledCalendarIds.set(savedIds);

        // Refresh token + load calendar list lazily in background
        // If the Lambda fails, the user stays connected and ensureValidToken() retries later
        this.refreshAccessToken(data.googleCalendarRefreshToken)
          .then(() => this.listCalendars().catch(() => { }))
          .catch(e => console.warn('[GoogleCalendar] Background token refresh failed (will retry on next use):', e));
      }
    } catch (e) {
      console.warn('[GoogleCalendar] Could not load stored settings', e);
    }
  }

  // ─── Connect: dispatches to the right flow for the current platform ──────
  async connect(): Promise<void> {
    if ((window as any).__TAURI_INTERNALS__) {
      return this.connectTauri();
    } else {
      return this.connectWeb();
    }
  }

  // ─── Tauri desktop flow (tauri-plugin-oauth + oauth://url event) ─────────
  private async connectTauri(): Promise<void> {
    this.connecting.set(true);
    let port: number | null = null;
    try {
      const clientId = await this.fetchClientId('desktop');

      port = await invoke<number>('plugin:oauth|start', {
        config: {
          response: `<!DOCTYPE html><html><head><title>Signed in</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:system-ui,sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;background:#f9fafb;color:#374151}</style>
</head><body>
<div style="text-align:center;padding:24px">
  <svg width="48" height="48" fill="none" stroke="#22c55e" stroke-width="2" viewBox="0 0 24 24" style="margin:0 auto 16px"><circle cx="12" cy="12" r="10"/><path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4"/></svg>
  <p style="font-size:18px;font-weight:600">Signed in successfully!</p>
  <p style="margin-top:8px;font-size:14px;color:#6b7280">You can close this window.</p>
</div>
<script>window.close();</script>
</body></html>`
        }
      });

      const redirectUri = `http://localhost:${port}`;
      const authUrl = this.buildAuthUrl(clientId, redirectUri);

      const code = await new Promise<string>(async (resolve, reject) => {
        const unlisten = await listen<string>('oauth://url', (event) => {
          unlisten();
          try {
            const cb = new URL(event.payload);
            const authCode = cb.searchParams.get('code');
            const oauthError = cb.searchParams.get('error');
            if (oauthError) reject(new Error(`Google OAuth error: ${oauthError}`));
            else if (authCode) resolve(authCode);
            else reject(new Error('No auth code in OAuth callback URL'));
          } catch (e) { reject(e); }
        });
        openUrl(authUrl).catch(err => { unlisten(); reject(err); });
      });

      await this.finaliseConnection(code, redirectUri, 'desktop');
    } catch (err) {
      if (port !== null) invoke('plugin:oauth|cancel', { port }).catch(() => { });
      throw err;
    } finally {
      this.connecting.set(false);
    }
  }

  // ─── Web browser flow (popup window + postMessage) ───────────────────────
  private async connectWeb(): Promise<void> {
    this.connecting.set(true);
    try {
      const clientId = await this.fetchClientId('web');
      const redirectUri = `${window.location.origin}/oauth-callback`;
      const authUrl = this.buildAuthUrl(clientId, redirectUri);

      // Open a centred popup
      const w = 520, h = 640;
      const left = Math.round(window.screenX + (window.outerWidth - w) / 2);
      const top = Math.round(window.screenY + (window.outerHeight - h) / 2);
      const popup = window.open(
        authUrl, 'google-oauth',
        `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,location=yes`
      );
      if (!popup) throw new Error('Popup blocked — please allow popups for this site.');

      const code = await new Promise<string>((resolve, reject) => {
        const onMessage = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;
          if (event.data?.type !== 'GOOGLE_OAUTH_CALLBACK') return;
          window.removeEventListener('message', onMessage);
          clearInterval(closedCheck);
          const { code: authCode, error } = event.data;
          if (error) reject(new Error(`Google OAuth error: ${error}`));
          else if (authCode) resolve(authCode);
          else reject(new Error('No auth code received from popup'));
        };
        window.addEventListener('message', onMessage);

        // Watch for the user closing the popup manually
        const closedCheck = setInterval(() => {
          if (popup.closed) {
            clearInterval(closedCheck);
            window.removeEventListener('message', onMessage);
            reject(new Error('Sign-in cancelled'));
          }
        }, 500);
      });

      await this.finaliseConnection(code, redirectUri, 'web');
    } finally {
      this.connecting.set(false);
    }
  }

  // ─── Shared OAuth helpers ─────────────────────────────────────────────────
  private async fetchClientId(platform: 'desktop' | 'web' = 'desktop'): Promise<string> {
    const { data: clientId, errors } = await this.client.queries.googleOAuthConfig({ platform });
    if (errors?.length || !clientId) throw new Error('Could not load Google OAuth configuration.');
    return clientId;
  }

  private buildAuthUrl(clientId: string, redirectUri: string): string {
    const url = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    url.searchParams.set('client_id', clientId);
    url.searchParams.set('redirect_uri', redirectUri);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('scope', CALENDAR_SCOPE + ' email profile');
    url.searchParams.set('access_type', 'offline');
    url.searchParams.set('prompt', 'consent');
    return url.toString();
  }

  private async finaliseConnection(code: string, redirectUri: string, platform: 'desktop' | 'web' = 'desktop'): Promise<void> {
    this.connectedPlatform = platform;
    const tokens = await this.exchangeCode(code, redirectUri, platform);
    const userInfo = await this.fetchUserInfo(tokens.access_token);
    this.googleEmail.set(userInfo.email ?? null);
    await this.saveTokens(tokens.refresh_token, tokens.access_token, tokens.expires_in);
    this.connected.set(true);
  }


  // ─── Disconnect ─────────────────────────────────────────────────────────
  async disconnect(): Promise<void> {
    this.accessToken = null;
    this.tokenExpiry = 0;
    this.connected.set(false);
    this.googleEmail.set(null);
    this.calendarList.set([]);
    this.enabledCalendarIds.set(null);

    if (this.settingsId) {
      await this.client.models.UserSettings.update({
        id: this.settingsId,
        googleCalendarRefreshToken: null,
        googleCalendarConnected: false,
      });
    }
  }

  // ─── Demo mode (no GCP needed) ──────────────────────────────────────────
  enableDemoMode(): void {
    this.isDemoMode.set(true);
    this.connected.set(true);
    this.googleEmail.set('demo@google.com');
  }

  disableDemoMode(): void {
    this.isDemoMode.set(false);
    this.connected.set(false);
    this.googleEmail.set(null);
  }

  // ─── List user's calendars ───────────────────────────────────────────────
  async listCalendars(): Promise<GoogleCalendarInfo[]> {
    await this.ensureValidToken();
    if (!this.accessToken) {
      console.warn('[GoogleCalendar] listCalendars: No access token available.');
      return [];
    }

    const res = await fetch(`${CALENDAR_API_BASE}/users/me/calendarList`, {
      headers: { Authorization: `Bearer ${this.accessToken}` },
    });
    if (!res.ok) {
      const errText = await res.text();
      console.error(`[GoogleCalendar] listCalendars failed with status ${res.status}:`, errText);
      return [];
    }
    const json = await res.json();
    const calendars = (json.items ?? []) as GoogleCalendarInfo[];
    this.calendarList.set(calendars);
    return calendars;
  }

  // ─── Persist the user's enabled calendar selection ──────────────────────
  async saveEnabledCalendars(ids: string[] | null): Promise<void> {
    this.enabledCalendarIds.set(ids);
    if (!this.settingsId) return;
    // Explicitly stringify so the AWSJSON round-trip is always consistent
    const { errors } = await this.client.models.UserSettings.update({
      id: this.settingsId,
      googleCalendarEnabledIds: ids !== null ? JSON.stringify(ids) : null as any,
    });
    if (errors?.length) {
      console.error('[GoogleCalendar] Failed to save calendar selection:', errors);
    }
  }

  // ─── Fetch calendar events for a date range ──────────────────────────────
  async getEvents(startDate: Date, endDate: Date): Promise<GoogleCalendarEvent[]> {
    if (!this.connected()) return [];

    // Demo mode — return realistic mock events for the requested day
    if (this.isDemoMode()) {
      return this.getMockEvents(startDate);
    }

    await this.ensureValidToken();
    if (!this.accessToken) return [];

    // Determine which calendars to fetch from
    const enabledIds = this.enabledCalendarIds();
    const calendarsToFetch = enabledIds !== null
      ? enabledIds
      : (this.calendarList().length > 0
        ? this.calendarList().map(c => c.id)
        : ['primary']);

    // Fetch events from all enabled calendars in parallel
    const results = await Promise.allSettled(
      calendarsToFetch.map(calId => this.fetchEventsForCalendar(calId, startDate, endDate))
    );

    return results
      .filter((r): r is PromiseFulfilledResult<GoogleCalendarEvent[]> => r.status === 'fulfilled')
      .flatMap(r => r.value)
      .sort((a, b) => {
        const aTime = a.start.dateTime ?? a.start.date ?? '';
        const bTime = b.start.dateTime ?? b.start.date ?? '';
        return aTime.localeCompare(bTime);
      });
  }

  private async fetchEventsForCalendar(
    calendarId: string, startDate: Date, endDate: Date
  ): Promise<GoogleCalendarEvent[]> {
    const url = new URL(`${CALENDAR_API_BASE}/calendars/${encodeURIComponent(calendarId)}/events`);
    url.searchParams.set('timeMin', startDate.toISOString());
    url.searchParams.set('timeMax', endDate.toISOString());
    url.searchParams.set('singleEvents', 'true');
    url.searchParams.set('orderBy', 'startTime');
    url.searchParams.set('maxResults', '50');

    const res = await fetch(url.toString(), {
      headers: { Authorization: `Bearer ${this.accessToken}` },
    });
    if (!res.ok) {
      console.error(`[GoogleCalendar] Failed to fetch events for ${calendarId}`, res.status);
      return [];
    }
    const json = await res.json();
    return ((json.items ?? []) as GoogleCalendarEvent[]).map(e => ({ ...e, calendarId }));
  }

  // ─── Private helpers ─────────────────────────────────────────────────────

  /** Token exchange — handled server-side via Lambda; client secret never in frontend */
  private async exchangeCode(
    code: string, redirectUri: string, platform: 'desktop' | 'web' = this.connectedPlatform
  ): Promise<{ access_token: string; refresh_token: string; expires_in: number }> {
    const { data: raw, errors } = await this.client.mutations.googleOAuthExchange({ code, redirectUri, platform });
    if (errors?.length || !raw) throw new Error('Token exchange failed: ' + JSON.stringify(errors));
    return JSON.parse(raw);
  }

  /** Token refresh — also server-side; client secret stays in Lambda env var */
  private async refreshAccessToken(refreshToken: string): Promise<void> {
    const platform = this.connectedPlatform;
    const { data: raw, errors } = await this.client.mutations.googleOAuthRefresh({ refreshToken, platform });
    if (errors?.length || !raw) throw new Error('Token refresh failed: ' + JSON.stringify(errors));
    const json = JSON.parse(raw);
    this.accessToken = json.access_token;
    this.tokenExpiry = Date.now() + (json.expires_in - 60) * 1000;
  }

  private async ensureValidToken(): Promise<void> {
    if (this.accessToken && Date.now() < this.tokenExpiry) return;
    if (!this.settingsId) return;

    const { data } = await this.client.models.UserSettings.get({ id: this.settingsId });
    if (data?.googleCalendarRefreshToken) {
      await this.refreshAccessToken(data.googleCalendarRefreshToken);
    }
  }

  private async fetchUserInfo(accessToken: string): Promise<{ email?: string }> {
    try {
      const res = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      return res.ok ? res.json() : {};
    } catch { return {}; }
  }

  private async saveTokens(
    refreshToken: string, accessToken: string, expiresIn: number
  ): Promise<void> {
    this.accessToken = accessToken;
    this.tokenExpiry = Date.now() + (expiresIn - 60) * 1000;

    // Ensure we have a settingsId — resolves it from Amplify auth if not set
    await this.resolveSettingsId();

    const { errors } = await this.client.models.UserSettings.update({
      id: this.settingsId!,
      googleCalendarRefreshToken: refreshToken,
      googleCalendarConnected: true,
    });
    if (errors?.length) {
      throw new Error('Could not save Google Calendar connection: ' + errors[0].message);
    }
  }

  /**
   * Self-heals settingsId when init() hasn't been called or user has no settings yet.
   * Queries current auth user → User record → UserSettings.
   */
  private async resolveSettingsId(): Promise<void> {
    if (this.settingsId) return;

    try {
      const { userId: cognitoId } = await getCurrentUser();

      // Find the User record by cognitoId
      const { data: users } = await this.client.models.User.list({
        filter: { cognitoId: { eq: cognitoId } }
      });
      if (!users.length) throw new Error('User record not found for current auth session.');
      const userId = users[0].id;

      // Find or create UserSettings
      const { data: settingsList } = await this.client.models.UserSettings.list({
        filter: { userId: { eq: userId } }
      });

      if (settingsList.length > 0) {
        this.settingsId = settingsList[0].id;
      } else {
        // No settings yet — create a minimal record so we can store the tokens
        const { data: created, errors } = await this.client.models.UserSettings.create({
          userId,
          secondsToAvoid: '5',
          googleCalendarConnected: false,
          displayModeForProject: 'LIST',
        } as any);
        if (errors?.length || !created) throw new Error('Could not create user settings.');
        this.settingsId = created.id;
      }
    } catch (e: any) {
      throw new Error('Could not resolve user settings: ' + (e?.message ?? e));
    }
  }

  private getMockEvents(date: Date): GoogleCalendarEvent[] {
    const d = (h: number, m = 0) => {
      const dt = new Date(date);
      dt.setHours(h, m, 0, 0);
      return dt.toISOString();
    };
    return [
      {
        id: 'mock-1',
        summary: 'Team Standup',
        description: 'Daily sync with the team',
        start: { dateTime: d(9, 0) },
        end: { dateTime: d(9, 30) },
      },
      {
        id: 'mock-2',
        summary: 'Client Review — SEL Studio',
        description: 'Monthly progress review with client',
        start: { dateTime: d(11, 0) },
        end: { dateTime: d(12, 0) },
      },
      {
        id: 'mock-3',
        summary: 'Lunch Break',
        start: { dateTime: d(12, 30) },
        end: { dateTime: d(13, 30) },
      },
      {
        id: 'mock-4',
        summary: 'Design Sprint',
        description: 'UX session for new dashboard',
        start: { dateTime: d(14, 0) },
        end: { dateTime: d(16, 0) },
      },
    ];
  }
}
